import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import Tippy from '@tippyjs/react';
import styled from 'styled-components';
import {
  Button,
  Card,
  Link,
  ResourceItem,
  ResourceList,
  Tabs,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import {
  AccessControl,
  NoDataCard,
  SkeletonTableLoading,
  getCurrentUserHook,
  getTranslatedText,
  isAuthorised,
  useActivityTracker,
  useIsAuthorisedHook,
  DateTimeFormatter
} from '@nile/components';
import { FormattedMessage, useIntl } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import SearchBox from '@nile/mangifera/src/Components/Search/SearchBox';
import { GET_SEARCH_DATA } from '@nile/banyan/src/Graphql/Mutations/search';
import { AccountClient } from '@nile/graphql';
import { DataGetter } from './utils';
import { convertMinutesToHours } from '@nile/mangifera/src/Utils/helpers';

const SLAComponent = (props) => {
  const data = props?.data;
  if (data?.resolutionViolated) {
    return (
      <div className="flex g3 actr">
        <div className="mt3">
          <SVGIcon name="HourglasswireEnd" color="#e72f23" size="xs" />
        </div>
        <div>
          <TextStyle variation="negative">
            <FormattedMessage id="ticket.details.insights.sla.overdue" />
          </TextStyle>
        </div>
        <div>
          <DateTimeFormatter type="dateTime" date={data?.resolutionDueTime} />
        </div>
      </div>
    );
  } else if (data?.resolutionPauseTime) {
    return (
      <div className="flex g3 actr">
        <div className="mt3">
          <SVGIcon name="HourGlassWirePaused" color="#929394" size="xs" />
        </div>
        <div>
          <TextStyle>
            <FormattedMessage id="ticket.details.insights.sla.paused" />
          </TextStyle>
        </div>
        <div>
          <DateTimeFormatter type="dateTime" date={data?.resolutionPauseTime} />
        </div>
      </div>
    );
  } else if (!data.resolutionViolated && data.resolutionTime) {
    return (
      <div className="flex g3 actr">
        <div className="mt3">
          <SVGIcon name="HourglasswireEnd" color="#929394" size="xs" />
        </div>
        <div>
          <TextStyle>
            <FormattedMessage id="ticket.details.insights.sla.achieved" />
          </TextStyle>
        </div>
        <div>
          <DateTimeFormatter type="dateTime" date={data?.resolutionTime} />
        </div>
      </div>
    );
  } else if (data?.resolutionDueTimeLeft) {
    const hoursToMins = convertMinutesToHours(data?.resolutionDueTimeLeft);
    return (
      <div className="flex g3 actr">
        <div className="mt3">
          <SVGIcon name="HourGlassWireStart" color="#929394" size="xs" />
        </div>

        <div>{hoursToMins}</div>
      </div>
    );
  } else {
    return <></>;
  }
};

const AssociateWorkItem = (props) => {
  const {
    timer,
    triggerElement,
    associatedWorkItem,
    reAssociateWorkItem,
    handleAssociateWorkItem,
    noPrefix,
    handleParentClose,
    highlight,
    showSla,
    otherDetails,
    offset
  } = props;
  const isAssociationAuthorised = useIsAuthorisedHook('serviceCatalog.view');
  const intl = useIntl();
  const entityTippyRef = useRef();
  const popupParentName = 'AssociateWorkItemParent';
  // const tabRef = useRef();
  // tabRef.current = useContext(TicketContext);
  const homeContext = useContext(HomeContext);
  const [mounted, setMounted] = useState(false);

  // const [associateEntityEnabled, setAssociateEntityEnabled] = useState(false);

  const closeAllOpenPopups = () => {
    if (
      document.getElementsByClassName(popupParentName) &&
      document.getElementsByClassName(popupParentName)?.length
    ) {
      Array.prototype.forEach.call(
        document.getElementsByClassName(popupParentName),
        (popup) => {
          if (popup?._tippy?.state?.isVisible) {
            popup?._tippy.hide();
          }
        }
      );
    }
  };

  function handleClickOutside(e) {
    if (
      !entityTippyRef.current?._tippy?.popper.contains(e.target) &&
      entityTippyRef.current?._tippy?.state?.isVisible
    ) {
      closePopover();
    }
  }

  function closePopover() {
    setTimeout(() => {
      setMounted(false);
      entityTippyRef.current._tippy.hide();
      // document.removeEventListener('click', handleClickOutside);
    }, 100);
  }

  const togglePopover = () => {
    if (entityTippyRef?.current?._tippy) {
      if (!entityTippyRef.current?._tippy?.state?.isVisible) {
        setMounted(true);
        if (props?.closeAllOpenPopups) {
          props?.closeAllOpenPopups();
        } else {
          closeAllOpenPopups();
        }
        entityTippyRef.current._tippy.show();
        // document.addEventListener('click', handleClickOutside);
      } else if (entityTippyRef.current?._tippy?.state?.isVisible) {
        // EventEmitter.dispatch('resetWorklogContent');
        closePopover();
      }
    }
  };

  const workItemTypeMap = {
    Ticket: 'TicketFilled',
    Project: 'ProjectFilled',
    TICKET: 'TicketFilled',
    PROJECT: 'ProjectFilled',
    TicketConversation: 'TicketFilled'
  };

  if (!isAssociationAuthorised) {
    return null;
  }

  return (
    <div className="flex center fg1">
      {!noPrefix && <SVGIcon name="AssociateEntity" />}
      <Tippy
        content={
          mounted ? (
            <SearchWorkItemPopup
              intl={intl}
              timer={timer}
              homeContext={homeContext}
              handleAssociateWorkItem={handleAssociateWorkItem}
              handleParentClose={handleParentClose}
              closePopup={closePopover}
            />
          ) : null
        }
        trigger="manual" // manually close the tip on api calls
        interactive
        placement="right"
        arrow={false}
        theme="light"
        allowHTML
        ref={(node) => {
          entityTippyRef.current = node;
        }}
        appendTo={document.body}
        offset={offset || [30, 30]}
        // onShow={() => {
        //   setAssociateEntityEnabled(true);
        // }}
        // onHide={() => {
        //   setAssociateEntityEnabled(false);
        // }}
        hideOnClick="toggle"
        maxWidth={500}
      >
        <div className="w100">
          <DataGetter
            className={`${popupParentName} fx actr ptr ${
              triggerElement ? '' : 'ml-10'
            } ${highlight ? 'timer-bg__highlight' : 'timer-bg'}`}
            onClick={
              reAssociateWorkItem
                ? togglePopover
                : !associatedWorkItem && togglePopover
            }
          >
            {triggerElement || (
              <div
                className={`fx actr ml-10 ${
                  !reAssociateWorkItem && associatedWorkItem
                    ? 'cursor-not-allowed'
                    : ''
                }`}
              >
                {associatedWorkItem && (
                  <>
                    <span className="fx actr">
                      <SVGIcon
                        name={workItemTypeMap[associatedWorkItem?.module]}
                        size="sm"
                      />
                    </span>
                  </>
                )}
                <TimeCollector
                  className={`fx actr br-5 line-height-35 ${
                    highlight ? 'timer-bg__highlight' : 'timer-bg'
                  }`}
                >
                  <span className="fx actr">
                    {associatedWorkItem?.subject ||
                      associatedWorkItem?.name || (
                        <span className="font-weight-600 opacity5">
                          {getTranslatedText(
                            intl,
                            'timer.empty.associate.entity'
                          )}
                        </span>
                      )}
                  </span>
                </TimeCollector>
              </div>
            )}
          </DataGetter>
          {showSla && otherDetails && (
            <div
              className={`flex actr fs10 g5 ml20 mtm-8 sla-component ${
                !reAssociateWorkItem ? 'cursor-not-allowed' : ''
              }`}
            >
              <div>
                <TextStyle variation="subdued">
                  <FormattedMessage id="mail.unknown.from.label" />
                </TextStyle>
              </div>
              <div>{otherDetails?.client?.name}</div>
              <div>
                <SLAComponent data={otherDetails} />
              </div>
            </div>
          )}
        </div>
      </Tippy>
    </div>
  );
};

export default AssociateWorkItem;

const SearchWorkItemPopup = (props) => {
  const {
    intl,
    timer,
    handleAssociateWorkItem,
    closePopup,
    homeContext,
    handleParentClose
  } = props;
  const currentUser = getCurrentUserHook();
  const [selected, setSelected] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchResult, setSearchResult] = useState(null);

  const tabs = [
    {
      id: '1',
      content: 'Tickets',
      moduleName: 'TICKET',
      authorisation: 'tickets.edit',
      // placeholder: getTranslatedText(intl, 'app.common.search.in', {
      //   name: getTranslatedText(intl, 'app.modules.workspace.ticket')
      // }),
      FeatureNameInBackend: 'TICKETING',
      newItemKey: 'admin.alertPolicy.actions.createTicket',
      newItemRole: 'tickets.add'
    },
    {
      id: '2',
      content: 'Project',
      moduleName: 'PROJECT',
      authorisation: 'project.view',
      // placeholder: getTranslatedText(intl, 'app.common.search.in', {
      //   name: getTranslatedText(intl, 'app.modules.workspace.project')
      // }),
      FeatureNameInBackend: 'PROJECT_MANAGEMENT',
      newItemKey: 'app.common.add.project',
      newItemRole: 'project.add'
    }
  ];

  const authorisedTabs = tabs.filter((tab) => {
    if (tab.authorisation) {
      return isAuthorised(
        currentUser?.role?.roleFeatureList,
        tab.authorisation
      );
    }
    return true;
  });

  const handleTabChange = (tab) => {
    setSelected(tab);
    if (searchResult) setSearchResult(null);
    if (searchText) setSearchText('');
  };

  const [onSearchAPI, { loading, error }] = useLazyQuery(GET_SEARCH_DATA, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted({ search }) {
      setSearchResult(search?.documents);
    },
    onError() {}
  });

  const checkNumber = (text) => {
    return /^\d+$/.test(text);
  };

  const onSearch = () => {
    onSearchAPI({
      variables: {
        term: searchText,
        modules: [authorisedTabs[selected]?.moduleName],
        pageInfo: {
          pageSize: 30
        }
      }
    });
  };

  const createTicketAndAssociate = () => {
    const projectTab = authorisedTabs[selected].moduleName === 'PROJECT';
    useActivityTracker({
      event: projectTab
        ? 'Created project from timer'
        : 'Created ticket from timer'
    });
    homeContext.openTab(
      projectTab
        ? {
            module: 'PROJECT_CREATION',
            tabId: 'create-project',
            url: '/project/B604yRELWW/new',
            data: {
              associateTimer: timer
            }
          }
        : {
            module: 'NEW',
            tabId: 'new-tab',
            url: '/ticket/aWqgT35s/new',
            data: {
              associateTimer: timer
            }
          }
    );
    closePopup();
    if (handleParentClose) handleParentClose('closeParent');
  };

  useEffect(() => {
    if (searchText.length > 2 || checkNumber(searchText)) {
      onSearch(searchText);
    }
  }, [searchText]);

  return (
    <Wrapper className="position-relative">
      <span className="close ptr position-absolute" onClick={closePopup}>
        <SVGIcon name="Close" size="sm" />
      </span>
      <Card AdaptHeight noShadow>
        <Tabs
          topBorder
          tabs={authorisedTabs}
          selected={selected}
          onSelect={handleTabChange}
        >
          <div className="border-bottom fx actr pl20 pr20 g10">
            <span className="fx actr jctr opacity6">
              <SVGIcon name="SearchNew" size="sm" />
            </span>
            <TextField
              autoFocus
              type="text"
              placeholder="Search Text"
              value={searchText}
              onChange={(val) => {
                setSearchText(val);
              }}
            />
          </div>
          <SearchResultContainer
            intl={intl}
            workItemType={authorisedTabs[selected]?.moduleName}
            loading={loading}
            closePopup={closePopup}
            searchResult={searchResult}
            selectedText={searchText}
            checkNumber={checkNumber}
            handleAssociateWorkItem={handleAssociateWorkItem.bind(
              this,
              authorisedTabs[selected]?.moduleName
            )}
          />
          {!timer?.workItem && timer?.timerId ? (
            <AccessControl
              roleToCheck={authorisedTabs[selected].newItemRole}
              render={
                <Link onClick={createTicketAndAssociate}>
                  <span className="ml-20 fx g10 actr">
                    <SVGIcon name="Add" color="#011fff" />
                    <FormattedMessage
                      id={authorisedTabs[selected].newItemKey}
                    />
                  </span>
                </Link>
              }
            />
          ) : null}
        </Tabs>
      </Card>
    </Wrapper>
  );
};

const SearchResultContainer = (props) => {
  const {
    intl,
    loading,
    workItemType,
    closePopup,
    searchResult,
    selectedText,
    checkNumber,
    handleAssociateWorkItem
  } = props;

  const associateWorkItem = (menulist) => {
    const displayData =
      menulist?.displayData?.ticket ||
      menulist?.displayData?.project ||
      menulist?.displayData;
    handleAssociateWorkItem({
      workId: menulist?.parentEntityId || menulist?.entityId,
      module: workItemType,
      client: menulist?.displayData?.client,
      ...displayData
    });
    closePopup();
  };

  return (
    <div className="ch-55 scroll ">
      <div className="fw fhp overflow-auto">
        {searchResult?.length > 0 ? (
          <ResourceList
            items={searchResult}
            renderItem={(menulist, index) => {
              return loading ? (
                <SkeletonTableLoading
                  tableHeader={false}
                  tableRow={2}
                  tableColumn={1}
                />
              ) : (
                <div>
                  <ResourceItem
                    id={menulist?.entityId}
                    key={menulist?.entityId}
                    onClick={() => associateWorkItem(menulist)}
                    gutter="xs"
                  >
                    <div className="px-20">
                      <SearchBox
                        menulist={menulist}
                        selectedModule={workItemType}
                        // tabContext={tabContext}
                      />
                    </div>
                  </ResourceItem>
                </div>
              );
            }}
          />
        ) : (
          <>
            {loading ? (
              <SkeletonTableLoading
                tableHeader={false}
                tableRow={2}
                tableColumn={1}
              />
            ) : (
              <>
                {selectedText?.length > 2 || checkNumber(selectedText) ? (
                  <div className="flex actr jctr fhp">
                    <NoDataCard
                      darkTheme
                      height="130px"
                      width="180px"
                      imageURL={`url(${`${envConfig.imageRoot}svg/search-new.svg`})`}
                      description={getTranslatedText(
                        intl,
                        'common.no.search.found'
                      )}
                    />
                  </div>
                ) : (
                  <>
                    {/* {recentSearchdResult?.recentHits?.length > 0 ? (
                      <> */}
                    {/* <p className="secondary-text pl-15 uppercase pb-5">
                          <FormattedMessage id="common.recent.search" />
                        </p>
                        <div className="pb-10">
                          <ResourceList
                            items={recentSearchdResult?.recentTerms}
                            renderItem={(
                              recentSearchTerms,
                              index
                            ) => {
                              return (
                                <div>
                                  <ResourceItem
                                    id={recentSearchTerms}
                                    key={recentSearchTerms}
                                    onClick={handleSearch.bind(
                                      this,
                                      recentSearchTerms
                                    )}
                                    gutter="xs"
                                    theme="dark"
                                  >
                                    <div className="flex actr pl10 pt10">
                                      <div>
                                        <SVGIcon
                                          name="SearchNew"
                                          size="md"
                                          color="#fff"
                                        />
                                      </div>
                                      <p className="subject-text pl-15">
                                        {recentSearchTerms}
                                      </p>
                                    </div>
                                  </ResourceItem>
                                </div>
                              );
                            }}
                          />
                        </div> */}
                    {/* <p className="secondary-text pl-15 uppercase pb-5">
                          <FormattedMessage id="common.search.results" />
                        </p> */}
                    {/* <ResourceList
                          items={recentSearchdResult?.recentHits}
                          renderItem={(recentSearchMenulist, index) => {
                            return (
                              <div>
                                <ResourceItem
                                  id={recentSearchMenulist?.entityId}
                                  key={recentSearchMenulist?.entityId}
                                  // onClick={handleCreation.bind(
                                  //   this,
                                  //   recentSearchMenulist
                                  // )}
                                  gutter="xs"
                                  theme="dark"
                                >
                                  <SearchBox
                                    menulist={recentSearchMenulist}
                                    tabContext={tabContext}
                                  />
                                </ResourceItem>
                              </div>
                            );
                          }}
                        /> */}
                    {/* </>
                    ) : ( */}
                    <div className="flex actr jctr fhp">
                      <NoDataCard
                        darkTheme
                        height="130px"
                        width="180px"
                        imageURL={`url(${`${envConfig.imageRoot}svg/search-new.svg`})`}
                        description={getTranslatedText(
                          intl,
                          'globaltimers.start.search.workItem'
                        )}
                      />
                    </div>
                    {/* )} */}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const TimeCollector = styled.div`
  min-height: 38px;
  min-width: 60px;
`;

const Wrapper = styled.div`
  width: 515px;
  height: 390px;
  .Superops-TextField__Input,
  .Superops-TextField__Input:hover,
  .Superops-TextField__Input:focus,
  .Superops-TextField__Input:focus:hover {
    border-bottom: none;
  }
  .Superops-Tabs__Panel {
    height: calc(100% - 50px);
    overflow: hidden;
  }
  .Superops-Tabs__Wrapper,
  .Superops-Tabs__WrapperPadded {
    border-bottom: 1px solid #0000001d;
    padding: 0 8px;
  }
  .Superops-Card {
    background: ${(props) =>
      props.highlight
        ? 'linear-gradient(105.43deg, #ffd0ec 1.25%, #e2a4ff 99.99%), #ffffff'
        : ''};
    transition: background: 200ms linear;

    height: 100%;

    & > div {
      height: 100%;
    }
  }
  .delete-timer, .close {
    right: 10px;
    top: 10px;
  }
`;
